import React, {useEffect, useState} from "react";
import {css} from "@emotion/react";
import lscache from "lscache";

import {elevation} from "@pg-design/elevation";
import {Col} from "@pg-design/grid-module";
import {borderRadius, flexAbsoluteCenter, onDesktop} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {useElementWasVisible} from "@pg-mono/hooks";

import {MID_LISTING_LAST_BANNER_TYPE} from "../../../../app/constants/storage_keys";
import {useMarketplaceArchitects} from "../../../../architect_offers/hooks/use_marketplace_architects";
import {useMarketplaceRegions} from "../../../../architect_offers/hooks/use_marketplace_regions";
import {OfferType} from "../../../types/OfferType";
import {ISelectedRegion} from "../../actions/offer_list_selected_data_actions";
import {disableDynamicPositionProps} from "../../hooks/use_list_dynamic_positions";
import {hitGtmArchitectCtaEvent} from "../../tracking/gtm_architect_cta_event";
import {GTMEventAction, hitGtmMapCtaEvent} from "../../tracking/gtm_map_cta_event";
import {MidListingArchitectBanner} from "./mid_listing_banners/MidListingArchitectBanner";
import {MidListingMapBanner} from "./mid_listing_banners/MidListingMapBanner";

interface IProps {
    offerType: OfferType | undefined;
    selectedRegions: ISelectedRegion[];
    offerCount: number | null;
    className?: string;
}

const MID_LISTING_BOX_ID = "listing-cta-box";

export const MidListingBannerBox = (props: IProps) => {
    const singleRegion = props.selectedRegions.length === 1 ? props.selectedRegions[0] : undefined;
    const {regionBySlug, marketplaceRegions} = useMarketplaceRegions(singleRegion?.slug);
    const marketplaceArchitects = useMarketplaceArchitects({
        withRegionOnly: true,
        regionId: regionBySlug?.id
    });
    const minPriceM2 = marketplaceArchitects[0]?.min_price_m2;
    const [bannerType, setBannerType] = useState<MidListingBannerType | null>(null);
    const isProperOfferTypeForArchitectOffer = props.offerType === OfferType.FLAT || props.offerType === OfferType.HOUSE || !props.offerType;
    const canBannerBeDisplayed = (regionBySlug && isProperOfferTypeForArchitectOffer) || props.offerCount;

    useElementWasVisible({
        ids: canBannerBeDisplayed && bannerType ? [MID_LISTING_BOX_ID] : [],
        onElementVisible: () => {
            if (bannerType === MidListingBannerType.MAP) {
                hitGtmMapCtaEvent(GTMEventAction.VIEW);
            }

            if (bannerType === MidListingBannerType.ARCHITECT) {
                hitGtmArchitectCtaEvent(GTMEventAction.VIEW);
            }
        },
        once: true
    });

    useEffect(() => {
        if (!marketplaceRegions?.length) {
            return;
        }

        const lastBannerType = lscache.get(MID_LISTING_LAST_BANNER_TYPE) || null;

        if (lastBannerType !== MidListingBannerType.ARCHITECT && regionBySlug && isProperOfferTypeForArchitectOffer) {
            setBannerType(MidListingBannerType.ARCHITECT);
            return;
        }

        if (props.offerCount) {
            setBannerType(MidListingBannerType.MAP);
            return;
        }

        setBannerType(null);
    }, [props.selectedRegions, marketplaceRegions?.length]);

    useEffect(() => {
        if (bannerType) {
            lscache.set(MID_LISTING_LAST_BANNER_TYPE, bannerType);
        }
    }, [bannerType]);

    return canBannerBeDisplayed ? (
        <Col sm={4} md={12} lg={16} as="li" {...disableDynamicPositionProps()} className={props.className}>
            {bannerType ? (
                <>
                    {bannerType === MidListingBannerType.MAP ? <MidListingMapBanner id={MID_LISTING_BOX_ID} offerCount={props.offerCount} /> : null}
                    {bannerType === MidListingBannerType.ARCHITECT && singleRegion?.slug ? (
                        <MidListingArchitectBanner offerType={props.offerType} regionSlug={singleRegion.slug} minPriceM2={minPriceM2} id={MID_LISTING_BOX_ID} />
                    ) : null}
                </>
            ) : (
                <div css={loaderWrapperStyle}>
                    <Loader />
                </div>
            )}
        </Col>
    ) : null;
};

export enum MidListingBannerType {
    MAP = "map",
    ARCHITECT = "architect"
}

const loaderWrapperStyle = css`
    height: 24rem;
    background-color: #f9faf4;
    ${elevation(2)};
    ${flexAbsoluteCenter};

    ${onDesktop(css`
        ${borderRadius(2)};
        height: 22.5rem;
    `)};
`;
